import axios from 'axios';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Toast } from '@capacitor/toast';
import { getPlatformName, isNativePlatform } from './device.util';
import { MIME_TYPES } from '../constants/common.constant';

/********************************** Native Code ********************************** */
const fileExistsInNative = async (path) => {
    try {
        await Filesystem.stat({
            path,
            directory: Directory.ExternalStorage,
        });
        return true;
    } catch {
        return false;
    }
}

const createDownloadDirectory = async () => {
    try {
        if (await getPlatformName() === 'android') {
            if (!(await fileExistsInNative('Download'))) {
                await Filesystem.mkdir({
                    path: 'Download',
                    directory: Directory.ExternalStorage,
                    recursive: true,
                });
            }
        }
    } catch (error) { }
}

const getUniqueFilename = async (dir, baseName) => {
    const extIndex = baseName.lastIndexOf('.');
    const name = extIndex !== -1 ? baseName.slice(0, extIndex) : baseName;
    const ext = extIndex !== -1 ? baseName.slice(extIndex) : '';

    let counter = 1;
    let uniqueName = baseName;

    while (await fileExistsInNative(`${dir ? dir + '/' : ''}${uniqueName}`)) {
        uniqueName = `${name}(${counter})${ext}`;
        counter++;
    }

    return uniqueName;
}

/************************** Download Methods **************************** */
const downloadFileFromBlob = async (blob, name, mimeType) => {
    let url = null
    if ((await getPlatformName()) !== "android") {
        const _blob = new Blob([blob], { type: mimeType });
        url = window.URL.createObjectURL(_blob)
    }
    await downloadFileUsingUrl(url, name, blob)
}

const downloadFileFromBuffer = (content, filename, mimeType) => {
    let byteArray;
    if (typeof content === 'string') {
        const base64String = content.replace(/-/g, '+').replace(/_/g, '/');
        const binaryString = atob(base64String);
        byteArray = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            byteArray[i] = binaryString.charCodeAt(i);
        }
    } else {
        byteArray = new Uint8Array(content);
    }

    const blob = new Blob([byteArray], { type: mimeType });
    downloadFileFromBlob(blob, filename, mimeType);
}

// const downloadFile = async (path, name, mimeType, type = 'private') => {
//     try {
//         if (await isNativePlatform()) await Toast.show({ text: 'Downloading...' });
//         const { data } = await axios({
//             url: '/api/common/setup/download-file',
//             method: 'POST',
//             data: {
//                 path: path
//             },
//             params: {
//                 type
//             },
//             responseType: 'blob'
//         });

//         downloadFileFromBlob(data, name, mimeType);
//     } catch (error) {
//         console.error(error);
//     }
// }

const downloadFileUsingUrl = async (url, filename = 'file', blob) => {
    if (await isNativePlatform()) {
        await Toast.show({ text: 'Saving...' });
        await createDownloadDirectory()
        const isAndroid = await getPlatformName() === 'android'
        const targetDir = isAndroid ? 'Download' : '';
        const uniqueFilename = await getUniqueFilename(targetDir, filename);
        if (blob) {
            const base64DataUrl = await blobToBase64(blob);
            const base64 = base64DataUrl.split(',')[1];
            await Filesystem.writeFile({
                path: `${isAndroid ? 'Download/' : ""}${uniqueFilename}`,
                data: base64,
                directory: Directory.ExternalStorage,
            });
        } else {
            await Filesystem.downloadFile({
                url: url,
                path: `${isAndroid ? 'Download/' : ""}${uniqueFilename}`,
                directory: Directory.ExternalStorage,
            });
        }
        return
    }

    Object.assign(document.createElement("a"), {
        href: url,
        download: filename
    }).click();

    window.URL.revokeObjectURL(url);
}

/******************************* file converter ******************************* */
function convertAttachmentToFile(attachment) {
    if (!attachment?.content) return null;

    const base64UrlData = attachment.content;
    const mimeType = attachment.type || 'application/octet-stream';
    const fileName = attachment.name || 'untitled';

    // Convert base64url to base64 (replace URL-safe chars)
    const base64 = base64UrlData.replace(/-/g, '+').replace(/_/g, '/');

    // Pad with "=" to make it a valid base64 string
    const paddedBase64 = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '=');

    try {
        const binaryStr = atob(paddedBase64);
        const len = binaryStr.length;
        const bytes = new Uint8Array(len);

        for (let i = 0; i < len; i++) {
            bytes[i] = binaryStr.charCodeAt(i);
        }

        return new File([bytes], fileName, { type: mimeType });
    } catch (err) {
        return null;
    }
}

const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject('Failed to read blob');
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
    });
};

const getFiles = async (attachments) => {
    const files = [];
    for (let i = 0; i < attachments.length; i++) {
        const attachment = attachments[i];
        const file = await convertAttachmentToFile(attachment);
        files.push(file)
    }
    return files;
}

const base64ToBlob = async  (base64, mimeType = 'application/octet-stream') => {
    const cleanBase64 = base64.includes(',')
        ? base64.split(',')[1]
        : base64;

    const byteCharacters = atob(cleanBase64);
    const byteArrays = [];

    for (let i = 0; i < byteCharacters.length; i += 512) {
        const slice = byteCharacters.slice(i, i + 512);
        const byteNumbers = new Array(slice.length);

        for (let j = 0; j < slice.length; j++) {
            byteNumbers[j] = slice.charCodeAt(j);
        }

        byteArrays.push(new Uint8Array(byteNumbers));
    }

    return new Blob(byteArrays, { type: mimeType });
};

const getMimeTypeFromFileName = async (fileName = '') => {
    if (typeof fileName !== 'string') return 'application/octet-stream';

    const cleaned = fileName.trim();

    if (!cleaned.includes('.')) {
        return 'application/octet-stream';
    }

    const ext = cleaned.split('.').pop().toLowerCase();

    return MIME_TYPES[ext] || 'application/octet-stream';
};

export {
    downloadFileFromBlob,
    convertAttachmentToFile,
    // downloadFile,
    downloadFileFromBuffer,
    downloadFileUsingUrl,
    getFiles,
    base64ToBlob,
    getMimeTypeFromFileName
}